<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h1 class="text-uppercase">
          <span>{{ isEditPage ? $t('edit') : $t('create') }} {{ $t('youtube_campaign') }}</span>
        </h1>
      </div>
      <div class="d-flex flex-row justify-content-center align-items-center gap-3 me-2">
        <div class="d-flex flex-row justify-content-center align-items-center gap-2" v-if="isSavingDraft">
          <Loader
            :size="'small'"
            :align="'start'"
          />
          {{ $t('saving_draft') }}
        </div>
        <Button
          class="btn btn-color10"
          v-b-modal.cancel-campaign-upsert-modal
        >
        {{ $t('cancel') }}
        </Button>
        <CancelCampaignUpsertModal
          :campaign="campaign"
          step="campaign"
        />
      </div>
    </div>
    <!-- CAMPAIGN FORM -->
    <div class="mt-3" v-if="!isEditPage || (isEditPage && campaign)">
      <div class="campaign-wrapper position-relative">
        <!-- INIT STEP -->
        <template v-if="step === 'init'">
          <div class="col-lg-8 mx-auto">
            <div class="card border-dashed border-color10">
              <div class="card-body">
                <p>
                  {{ $t('easy_campaign_pixel_roads') }}
                </p>
                <p>
                  {{ $t('important_thoughts_for_campaign') }}
                </p>
                <ul>
                  <li>{{ $t('campaign_duration') }}</li>
                  <li>{{ $t('available_budget') }}*<br/>
                    <span class="font-size-xSmall">* {{ $t('daily_budget_calculation') }}</span>
                  </li>
                  <li>{{ $t('suggest_youtube_link') }}</li>
                  <li>{{ $t('suggest_pixel_pre_installed') }}</li>
                  <li>{{ $t('youtube_campaign_creation_sla') }}</li>
                </ul>
                <div class="mt-4 text-center">
                  <Button
                    class="btn btn-color10"
                    @click="changeStep('campaign')"
                  >
                    {{ $t('start2') }}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </template> <!-- END INIT STEP -->
        <!-- HEADING -->
        <section class="my-4" v-if="step !== 'init'">
          <div class="text-center">
            <p>
              {{ stepDescription }}
              <span
                v-b-tooltip.hover
                :title="tooltips.pixels"
                v-if="step === 'tracking'"
              >
                <i class="fa fa-info-circle font-size-xRegular"></i>
              </span>
            </p>
            <div class="mb-3 text-start">
              <a href="#" class="link-color2" @click.prevent="changeStep(previousStep)" v-if="previousStep"><i class="fa fa-angle-left me-2"></i>{{ $t('back') }}</a>
            </div>
          </div>
        </section> <!-- END HEADING -->
        <!-- CAMPAIGN STEP -->
        <template v-if="step === 'campaign'">
          <section class="mb-5">
            <div class="accordion accordion-flush" id="accordionFlushExample">
              <!-- FORM CAMPAIGN ACCORDION ITEM -->
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" block v-b-toggle.accordion-1 variant="info">
                    {{ $t('campaign') }}
                  </button>
                </h2>
                <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <form @submit.prevent="" class="default-form">
                      <div class="d-flex flex-column flex-md-row">
                        <div class="col-12 col-md-6 pe-md-10">
                          <!-- GENERAL DATA HEADER -->
                          <div class="mb-2">
                            <div class="d-flex align-items-center justify-content-between">
                              <div>
                                <h3 class="text-uppercase font-size-regular fw-500">
                                  <span>{{ $t('info_from_your_campaign') }}</span>
                                </h3>
                              </div>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <!-- ADVERTISER -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">
                              {{ $t('advertiser') }}*
                            </label>
                            <div v-if="advertisersLoader">
                              <Loader
                                :size="'small'"
                                :align="'start'"
                              />
                            </div>
                            <template v-else>
                              <div class="custom-select">
                                <select
                                  class="form-control"
                                  v-model.trim="form.advertiser_id"
                                  :disabled="campaign !== null && campaign.status !== 'DRAFT'"
                                >
                                  <option disabled value="">{{ $t('select') }}</option>
                                  <template v-if="advertisers.length > 0">
                                    <option v-for="(lAdvertiser, index) in advertisers" :key="index" :value="lAdvertiser.id">{{ lAdvertiser.name }}</option>
                                  </template>
                                </select>
                              </div>
                              <p
                                class="font-size-xSmall fw-bold mt-2 error-message-card"
                                v-if="selectedAdvertiser && selectedAdvertiser.status === 'ERROR'"
                              >
                                {{ $t('advertiser_creation_issue_access_page_try_again', {name:selectedAdvertiser.name}) }}
                              </p>
                            </template>
                          </div> <!-- END ADVERTISER -->
                          <!-- NAME -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('campaign_name') }}*</label>
                            <input type="text" class="form-control" :placeholder="$t('campaign_name')" v-model.trim="form.name" />
                          </div> <!-- END NAME -->
                          <!-- DATES -->
                          <div class="form-group mb-7">
                            <label class="form-label fw-bold">{{ $t('duration') }}*</label>
                            <div class="d-flex">
                              <!-- START DATE -->
                              <div class="col-lg-4">
                                <DateTimeInput
                                  v-model.trim="form.start_date"
                                  format="dd/MM/yyyy"
                                  type="date"
                                  theme="basic"
                                  class="me-2"
                                  :placeholder="$t('start')"
                                  :readonly="campaign && campaign.has_started"
                                />
                              </div> <!-- END START DATE -->
                              <!-- END DATE -->
                              <div class="col-lg-4">
                                <DateTimeInput
                                  v-model.trim="form.end_date"
                                  format="dd/MM/yyyy"
                                  type="date"
                                  theme="basic"
                                  class="mx-2"
                                  :placeholder="$t('end')"
                                />
                              </div> <!-- END END DATE -->
                            </div>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.start_date && !isCampaignStartDateValid"
                            >
                              {{ $t('start_date_not_before_today') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.end_date && !isCampaignEndDateValid"
                            >
                              {{ $t('end_date_validation') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.start_date && form.end_date && isCampaignEndDateBeforeStartDate"
                            >
                              {{ $t('end_date_not_before_start_date') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 alert-message-card"
                              v-if="form.start_date && form.end_date && isCampaignPeriodBelowRecommended"
                            >
                              {{ $t('campaign_minimum_duration_10_days') }}
                            </p>
                          </div> <!-- END DATES -->
                          <!-- GENERAL DATA HEADER -->
                          <div class="my-2">
                            <div class="d-flex align-items-center justify-content-between">
                              <div>
                                <h3 class="text-uppercase font-size-regular fw-500">
                                  <span>{{ $t('budget') }}</span>
                                </h3>
                              </div>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <!-- AMOUNT -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('campaign_budget') }}*</label>
                            <Money
                              class="form-control mb-3"
                              v-model.trim="form.amount"
                              v-bind="money"
                            />
                            <template v-if="selectedAdvertiser">
                              <div class="d-flex flex-column flex-wrap">
                                <div class="">
                                  <p
                                    class="font-size-xSmall fw-bold mt-2 error-message-card"
                                    v-if="hasCampaignExceededAvailableAdvertiserAmount"
                                  >
                                    {{ $t('campaign_budget_cannot_exceed') }}
                                  </p>
                                  <p class="font-size-xSmall fw-bold">
                                    {{ $t('available_balance') }}: {{ selectedAdvertiser.available_amount / 100 | formatMoney(selectedAdvertiser.currency) }}
                                  </p>
                                </div>
                                <div class="text-lg-end d-flex align-items-center justify-content-center justify-content-lg-end">
                                  <Button
                                    class="btn btn-color10 btn-xs me-2"
                                    @click="addAllAvailableAmountToCampaign"
                                  >
                                    {{ $t('use_all_balance') }}
                                  </Button>
                                </div>
                              </div>
                            </template>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="!checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                            >
                              {{ $t('recommended_minimum_daily_budget', {value:minimumDailyBudget, date:this.$options.filters.formatDate(suggestedEndDate)}) }}
                            </p>
                          </div><!-- END AMOUNT -->
                          <!-- CPC TARGET AMOUNT -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">
                              {{ $t('cpv_goal') }}*
                              <span v-b-tooltip.hover.html :title="tooltips.cpv_goal"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                            </label>
                            <Money
                              class="form-control mb-3"
                              v-model.trim="form.cpv_target_amount"
                              v-bind="money"
                            />
                            <p class="font-size-xSmall fw-bold error-message-card" v-if="form.cpv_target_amount !== '' && form.cpv_target_amount < 0.08">
                              {{ $t('minimum_cpv_goal_value') }} {{ 0.08 | formatMoney(selectedAdvertiser ? selectedAdvertiser.currency : null) }}
                            </p>
                          </div><!-- END CPC TARGET AMOUNT -->
                        </div>
                        <div class="col-12 col-md-6 ps-md-5">
                          <!-- GENERAL DATA HEADER -->
                          <div class="mb-2">
                            <div class="d-flex align-items-center justify-content-between">
                              <div>
                                <h3 class="text-uppercase font-size-regular fw-500">
                                  <span>{{ $t('segmentation') }}</span>
                                </h3>
                              </div>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <!-- BROWSER -->
                          <div class="mb-2">
                            <label class="form-label fw-bold w-100 d-block">{{ $t('devices') }}*</label>
                            <div class="form-group mb-3 me-2 d-flex">
                              <div class="form-check form-check-inline">
                                <input
                                  class="form-check-input"
                                  type="checkbox"
                                  :id="`browser-mobile`"
                                  value="MOBILE"
                                  v-model="form.browser"
                                >
                                <label class="form-check-label" :for="`browser-mobile`">{{ $t('mobile') }}</label>
                              </div>
                              <div class="form-check form-check-inline">
                                <input
                                  class="form-check-input"
                                  type="checkbox"
                                  :id="`browser-desktop`"
                                  value="DESKTOP"
                                  v-model="form.browser"
                                >
                                <label class="form-check-label" :for="`browser-desktop`">{{ $t('desktop') }}</label>
                              </div>
                            </div>
                          </div> <!-- END BROWSER -->
                          <div class="">
                            <!-- COUNTRIES -->
                            <div class="form-group mb-3 me-2">
                              <div class="d-flex align-items-center mb-2">
                                <label class="form-label fw-bold mb-0">
                                  {{ $t('countries') }}
                                  <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                                </label>
                              </div>
                              <template v-if="countriesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="selectedCountries"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="countryOptions"
                                  :multiple="true"
                                />
                              </template>
                            </div> <!-- END COUNTRIES -->
                            <!-- STATES -->
                            <div class="form-group mb-3 me-2" v-if="selectedCountries.length > 0">
                              <div class="d-flex align-items-center mb-2">
                                <label class="form-label fw-bold mb-0">
                                  {{ $t('states') }}
                                  <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                                </label>
                                <Button
                                  class="btn-xs btn-color10 py-1 ms-2"
                                  @click="selectAllStates"
                                  v-if="stateOptions.length > 0"
                                >
                                  {{ $t('select_all') }}
                                </Button>
                              </div>
                              <template v-if="statesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="form.states"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="stateOptions"
                                  :multiple="true"
                                  :disabled="selectedCountries.length == 0"
                                />
                                <div
                                  class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
                                  v-if="selectedCountries.length == 0"
                                >
                                  <p
                                    class="mb-0"
                                  >
                                    {{ $t('select_at_least_one_country_for_state_selection') }}
                                  </p>
                                </div>
                              </template>
                            </div> <!-- END STATES -->
                            <!-- CITIES -->
                            <div class="form-group mb-3" v-if="selectedCountries.length > 0">
                              <label class="form-label fw-bold">
                                {{ $t('cities') }}
                                <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                              </label>
                              <template v-if="citiesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="form.cities"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="citiesOptions"
                                  :multiple="true"
                                  :disabled="selectedCountries.length == 0"
                                />
                                <div
                                  class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
                                  v-if="selectedCountries.length == 0"
                                >
                                  <p
                                    class="mb-0"
                                  >
                                    {{ $t('select_at_least_one_country_for_city_selection') }}
                                  </p>
                                </div>
                              </template>
                            </div> <!-- END CITIES -->
                            <div v-if="showAlertOnCitiesAndStatesSelection && (!countriesLoader && !citiesLoader && !statesLoader)">
                              <p class="fw-bold alert-message-card text-color2 font-size-xRegular mt-2" v-html="showAlertOnCitiesAndStatesSelection">
                              </p>
                            </div>
                            <!-- AUDIENCE LIST -->
                            <div class="form-group mt-5 mb-3" v-if="selectedAdvertiser">
                              <label class="form-label fw-bold w-100 d-block">
                                {{ $t('broad_audience_list') }}
                                <span v-b-tooltip.hover :title="tooltips.platform_audiences"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                                <Button @click.prevent="" class="btn-xs btn-color10 py-1 ms-2" v-b-modal="'select-platform-audience-modal'">{{ $t('select_audiences') }}</Button>
                              </label>
                              <template v-if="form.platform_audiences.length === 0">
                                <p>{{ $t('zero_selected_audiences') }}</p>
                              </template>
                              <template v-else>
                                <ul>
                                  <li class="d-flex justify-content-between align-items-center mb-3 col-lg-10" v-for="(audience, index) in form.platform_audiences" :key="index">
                                    <span class="col-lg-11">
                                      <span class="d-flex align-items-center">
                                        {{ audience.name}} <span class="star-icon ms-2 font-size-xSmall"><i class="fa fa-star"></i></span>
                                      </span>
                                    </span>
                                    <div class="col-lg-1 ms-2">
                                      <Button
                                        class="btn-xs btn-circle btn-danger font-size-small p-0"
                                        @click="destroyAudience(index, 'PLATFORM_DATA')"
                                        v-b-tooltip.hover :title="$t('remove')"
                                      >
                                        <i class="fa fa-trash px-3"></i>
                                      </Button>
                                    </div>
                                  </li>
                                </ul>
                              </template>
                              <SelectAudienceModal
                                :selected="form.platform_audiences"
                                type="PLATFORM_DATA"
                                @audiences-selected="handleAudienceListSelected(...arguments, 'PLATFORM_DATA')"
                              />
                            </div> <!-- END AUDIENCE LIST -->
                          </div>
                        </div>
                      </div>
                      <!-- ACTION -->
                      <div class="mt-4 text-center">
                        <Button
                          class="btn btn-color10"
                          @click="changeAccordionItem('accordion-2')"
                          :disabled="!isCampaignStepComplete || !checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                        >
                        {{ $t('next') }}
                        </Button>
                      </div> <!-- END ACTION -->
                    </form>
                  </div>
                </b-collapse>
              </div> <!-- END FORM CAMPAIGN ACCORDION ITEM -->

              <!-- CREATIVE ACCORDION ITEM -->
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button
                    class="accordion-button text-uppercase font-size-xMedium"
                    type="button" block v-b-toggle.accordion-2 variant="info"
                    :disabled="!isCampaignStepComplete || !checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                  >
                    {{ $t('creative') }}
                  </button>
                </h2>
                <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <!-- GENERAL DATA HEADER -->
                    <div class="my-2 mt-4">
                      <div class="d-flex align-items-center justify-content-between">
                        <div>
                          <h3 class="text-uppercase font-size-regular fw-500">
                            <span>{{ $t('video') }}</span>
                          </h3>
                        </div>
                      </div>
                    </div> <!-- END GENERAL DATA HEADER -->
                    <form @submit.prevent="" class="default-form">
                      <!-- VIDEO -->
                      <div class="form-group mb-3">
                        <label class="form-label fw-bold">{{ $t('video_id') }}</label>
                        <input type="text" class="form-control" placeholder="69o-O8WB-NU" v-model.trim="form.creative.video_id" />
                        <div class="mt-2 alert-message-card text-color2 px-3 py-2 font-size-xSmall">
                          <p class="mb-0">
                            {{ $t('youtube_video_id_explanation') }} https://www.youtube.com/watch?v=<strong>69o-O8WB-NU</strong>
                          </p>
                        </div>
                        <template v-if="showVideoPreview">
                          <div class="mt-3 text-center">
                            <iframe width="560" height="315" :src="`https://www.youtube.com/embed/${form.creative.video_id}`" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
                          </div>
                          <!-- NAME -->
                          <div class="form-group">
                            <label class="form-label fw-bold">{{ $t('headline') }}</label>
                            <input type="text" class="form-control" :placeholder="$t('headline')" v-model.trim="form.creative.headline" />
                            <p class="font-size-xSmall text-end mt-1 mb-0"
                              :class="{
                                'text-red': form.creative.headline.length > 90
                              }"
                            >{{ `${ form.creative.headline.length }/90` }}</p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.creative.headline.length > 90"
                            >
                              {{ $t('headline_cannot_contain_more_than_90_characters') }}
                            </p>
                          </div> <!-- END NAME -->
                          <!-- DESCRIPTION -->
                          <div class="form-group">
                            <label class="form-label fw-bold">{{ $t('description') }}</label>
                            <input type="text" class="form-control" :placeholder="$t('description')" v-model.trim="form.creative.description" />
                            <p class="font-size-xSmall text-end mt-1 mb-0"
                              :class="{
                                'text-red': form.creative.description.length > 70
                              }"
                            >{{ `${ form.creative.description.length }/70` }}</p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.creative.description.length > 70"
                            >
                              {{ $t('description_cannot_contain_more_than_characters', { count: 70 }) }}
                            </p>
                          </div> <!-- END DESCRIPTION -->
                          <!-- CTA -->
                          <div class="form-group">
                            <label class="form-label fw-bold">
                              {{ $t('call_to_action') }}
                              <span
                                id="cta-tooltip"
                              >
                                <i class="fa fa-info-circle font-size-xRegular"></i>
                              </span>
                            </label>
                            <b-tooltip target="cta-tooltip">
                              <strong>{{ $t('examples') }}</strong><br/>
                              {{ $t('click_here') }}<br/>
                              {{ $t('learn_more') }}<br/>
                              {{ $t('contact_us2') }}
                            </b-tooltip>
                            <input type="text" class="form-control" :placeholder="$t('call_to_action')" v-model.trim="form.creative.call_to_action" />
                            <p class="font-size-xSmall text-end mt-1 mb-0"
                              :class="{
                                'text-red': form.creative.call_to_action.length > 10
                              }"
                            >{{ `${ form.creative.call_to_action.length }/10` }}</p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.creative.call_to_action.length > 10"
                            >
                              {{ $t('call_to_action_cannot_contain_more_than_characters', { count: 10 }) }}
                            </p>
                          </div> <!-- END CTA -->
                          <!-- URL -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('destination_url') }}</label>
                            <div class="input-group mb-3">
                              <span class="input-group-text">https://</span>
                              <input type="text" class="form-control" placeholder="url.com.br" v-model.trim="form.creative.url" />
                            </div>
                          </div> <!-- END URL -->
                        </template>
                      </div><!-- END VIDEO -->
                      <!-- ACTIONS -->
                      <div class="text-center mt-4">
                        <Button
                          class="btn btn-color10"
                          @click="changeAccordionItem('accordion-3')"
                          :disabled="!isCreativeStepComplete"
                        >
                          {{ $t('next') }}
                        </Button>
                      </div> <!-- END ACTION -->
                    </form>
                  </div>
                </b-collapse>
              </div> <!-- END CREATIVE ACCORDION ITEM -->

              <!-- PIXEL ACCORDION ITEM -->
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" type="button" block v-b-toggle.accordion-3 variant="info" :disabled="!isCreativeStepComplete">
                    {{ $t('pixels') }}
                  </button>
                </h2>
                <b-collapse id="accordion-3" accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <div class="mb-5">
                      <p
                        class="text-center font-size-xSmall fw-bolder mt-2 alert-message-card"
                      >
                        {{ $t('select_pixel_message') }}
                      </p>
                      <p
                        class="text-center font-size-xSmall fw-bolder mt-2"
                      >
                        {{ $t('learn_more_about_pixel') }}, <a href="https://pixelroads.com.br/blog/pixel-academy/1-8-1-explicacao-de-pixels/" target="_blank">{{ $t('click_here') }}</a>
                      </p>
                      <!-- ACTION -->
                      <div class="text-center" v-if="selectedAdvertiser">
                        <router-link
                          :to="{ name: 'pixels.index' }"
                          class="btn btn-color10 mb-4 me-2 btn-sm"
                          target="_blank"
                        >
                          {{ $t('add_new_pixel') }}
                        </router-link>
                        <div class="border border-dashed border-color10 p-5">
                          <SelectPixelRule
                            :advertiserId="form.advertiser_id"
                            @pixel-rule-selected="handleSelectedPixelRule"
                          />
                        </div>
                      </div> <!-- END ACTION -->
                      <template v-if="form.pixels.length > 0">
                        <div class="mt-4 text-center border border-dashed border-color10 p-5">
                          <h2 class="text-uppercase font-size-regular fw-500">
                            <span>{{ $t('selected_pixels') }}</span>
                          </h2>
                          <table class="table table-striped align-middle table-row-dashed fs-6 gy-2 dataTable no-footer">
                            <thead>
                              <tr>
                                <th>{{ $t('pixel_name') }}</th>
                                <th>{{ $t('rule_name') }}</th>
                                <th>{{ $t('objective') }}</th>
                                <th>{{ $t('event_name') }}</th>
                                <th width="100">{{ $t('actions') }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr class="text-nowrap" v-for="(rule, index) in form.pixels" :key="index">
                                <td>{{ rule.pixel.name }}</td>
                                <td>{{ rule.name }}</td>
                                <td>{{ rule.objective | formatObjective }}</td>
                                <td>{{ rule.event_name }}</td>
                                <td>
                                  <Button
                                    class="btn-danger btn-xs font-size-small btn-circle p-0 mb-2"
                                    @click.prevent="openDestroyPixelRuleModal(rule)"
                                    v-b-tooltip.hover :title="$t('remove_rule')"
                                  >
                                    <i class="fa fa-trash px-3"></i>
                                  </Button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </template>
                      <DestroyPixelRuleModal
                        :rule="selectedPixelRule"
                        @pixel-rule-destroyed="destroyPixelRule"
                      />
                    </div>
                  </div>
                </b-collapse>
              </div> <!-- PIXEL ACCORDION ITEM -->
            </div>
          </section>
          <!-- ACTION -->
          <div class="text-center">
          <div>
            <!-- MESSAGES -->
            <div
              class="mb-2 alert-message-card text-color2 font-size-small px-3 py-2"
              v-if="loader"
            >
              <p
                class="mb-0"
              >
                {{ $t('this_action_may_take_a_few_minutes') }}
              </p>
            </div> <!-- END MESSAGES -->
            <Button
              class="btn btn-color10"
              @click="clickSubmit"
              :disabled="!isFormComplete || isSavingDraft"
              :loading="loader"
            >
              {{ campaign ? $t('save_campaign') : $t('create_campaign') }}
            </Button>
          </div>
        </div> <!-- END ACTION -->
        </template> <!-- END CAMPAIGN STEP -->
      </div>
    </div> <!-- END CAMPAIGN FORM -->
  </div>
</template>

<script>

import AdvertisersService from '@/modules/advertisers/services/advertisers-service'
import CampaignsService from '@/modules/campaigns/services/campaigns-service'
import { getMoneyFormat } from '@/utils'
import HelpersService from '@/modules/helpers/services/helpers-service'
import NotificationService from '@/modules/notifications/services/notifications-service'
import variables from '@/mixins/variables'

import Button from '@/components/common/Button/Button'
import CancelCampaignUpsertModal from '@/components/campaigns/Modals/CancelCampaignUpsertModal'
import DateTimeInput from '@/components/common/Input/DateTimeInput'
import DestroyPixelRuleModal from '@/components/campaigns/UpsertForm/DestroyPixelRuleModal'
import Loader from '@/components/common/Loader/Loader'
import { Money } from 'v-money'
import Multiselect from 'vue-multiselect'
import SelectAudienceModal from '@/components/audiences/SelectAudienceModal'
import SelectPixelRule from '@/components/pixels/SelectPixelRule'

import { mapState } from 'vuex'
import moment from 'moment'

export default {
  mixins: [variables],
  props: ['baseCampaign'],
  components: {
    Button,
    CancelCampaignUpsertModal,
    DateTimeInput,
    DestroyPixelRuleModal,
    Loader,
    Money,
    Multiselect,
    SelectAudienceModal,
    SelectPixelRule
  },
  data () {
    return {
      autoSaveInterval: null,
      advertisers: [],
      advertisersLoader: false,
      campaign: null,
      cities: [],
      citiesLoader: false,
      countries: [],
      countriesLoader: false,
      form: {
        advertiser_id: '',
        amount: '',
        cpv_target_amount: 0.08,
        browser: [],
        cities: [],
        platform_audiences: [],
        creative: {
          video_id: '',
          headline: '',
          description: '',
          call_to_action: '',
          url: ''
        },
        end_date: '',
        name: '',
        pixels: [],
        start_date: '',
        states: [],
        type: 'YOUTUBE'
      },
      isSavingDraft: false,
      loader: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },
      selectedPixelRule: '',
      selectedCountries: [],
      states: [],
      statesLoader: false,
      step: 'init',
      showVideoPreview: false,
      minimumDailyBudget: 10
    }
  },
  mounted () {
    if (this.isEditPage) {
      this.step = 'campaign'
      this.getCampaign()
    }
    if (this.user) this.getPageContent()
    // this.setAutoSaveInterval()
  },
  beforeDestroy () {
    // this.clearAutoSaveInterval()
  },
  watch: {
    user (newUser) {
      this.getPageContent()
    },
    /**
     * Watch for changes to countries
     */
    countries (newValue, oldValue) {
      // If is edit page, set selected countries
      if (this.isEditPage) this.setSelectedCountries()
    },
    /**
     * Watch for changes on video_id
    */
    'form.creative.video_id' (newValue, oldValue) {
      this.showVideoPreview = false
      if (newValue) this.loadVideoPreview(newValue)
    },
    /**
     * Watch for changes to selected countries
     */
    selectedCountries (newValue, oldValue) {
      this.getStates()
      this.getCities()
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    /**
     *  check if daily budget is bigger than minimum daily budget
     */
    checkIfDailyBudgetIsBiggerThanMinimumDailyBudget () {
      if (this.suggestedEndDate && this.form.amount > 0) {
        const startDate = moment(this.form.start_date)
        const endDate = moment(this.form.end_date)
        const campaignDays = endDate.diff(startDate, 'days') + 1
        const totalBudget = campaignDays * this.minimumDailyBudget

        return this.form.amount >= totalBudget
      }
      return true
    },
    /**
     * Get cities options based on the selected states of the line
     */
    citiesOptions () {
      /* const states = this.form.states.map(state => {
        return state.label
      }) */
      const cities = this.cities.reduce((filtered, item) => {
        /* if (states.includes(item.state_name)) {
          filtered.push({
            label: `${item.name} (${item.state_short_name})`,
            code: item.id
          })
        } */
        filtered.push({
          label: `${item.name} (${item.state_short_name} - ${item.country_short_name})`,
          code: item.id
        })
        return filtered
      }, [])
      return cities
    },
    /**
     * Get countries options
     */
    countryOptions () {
      return this.countries
        .filter(item => ['BR', 'AR', 'PY', 'US'].includes(item.short_name))
        .map(item => {
          return {
            label: `${item.name} (${item.short_name})`,
            code: item.id
          }
        })
    },
    /**
     * Check if campaign amount has exceeded what advertiser has available
     */
    hasCampaignExceededAvailableAdvertiserAmount () {
      if (!this.selectedAdvertiser) return true
      // If is new campaign, the only thing that matters is the full available amount
      const amount = this.form.amount * 100
      const available = this.selectedAdvertiser.available_amount
      if (!this.campaign) {
        return amount > available
      }

      // If is campaign that already exists, calculate the difference between new amount and old amount and compare that value to available amount
      const difference = amount - this.campaign.amount
      return difference > available
    },
    /**
     * Is campaign end date valid
     */
    isCampaignEndDateValid () {
      if (!this.form.end_date) return false
      const date = moment(this.form.end_date)
      // Check if is update and if value hasnt changed from original
      if (this.campaign && !this.isCampaignDraft) {
        const then = moment(this.campaign.end_date)
        if (date.isSame(then)) return true
      }
      const tomorrow = moment().startOf('day').add(1, 'day')
      return date.isSameOrAfter(tomorrow)
    },
    /**
     * Is campaign status draft
     */
    isCampaignDraft () {
      return this.campaign.status === 'DRAFT'
    },
    /**
     * Checks if campaign end date is before start date
     */
    isCampaignEndDateBeforeStartDate () {
      if (!this.form.end_date || !this.form.start_date) return true
      const startDate = moment(this.form.start_date)
      const endDate = moment(this.form.end_date)

      return endDate.isSameOrBefore(startDate)
    },
    /**
     * Check if campaign period is below recommended
     */
    isCampaignPeriodBelowRecommended () {
      if (!this.form.start_date || !this.form.end_date) return true
      const start = moment(this.form.start_date)
      const end = moment(this.form.end_date)
      return end.diff(start, 'days') < 10
    },
    /**
     * Is campaign start date valid
     */
    isCampaignStartDateValid () {
      if (!this.form.start_date) return false
      const date = moment(this.form.start_date)
      // Check if is update of started campaign and if value hasnt changed from original
      if (this.campaign && !this.isCampaignDraft) {
        const then = moment(this.campaign.start_date)
        if (date.isSame(then)) return true
      }
      const today = moment().endOf('day')
      return date.isAfter(today)
    },
    /**
     * Check if campaign is complete
     */
    isCampaignStepComplete () {
      if (
        !this.form.advertiser_id ||
        !this.form.name ||
        !this.form.start_date ||
        !this.form.end_date ||
        parseFloat(this.form.amount) <= 0 ||
        this.hasCampaignExceededAvailableAdvertiserAmount ||
        parseFloat(this.form.cpv_target_amount) < 0.08 ||
        !this.isCampaignStartDateValid ||
        !this.isCampaignEndDateValid ||
        this.isCampaignEndDateBeforeStartDate ||
        !this.selectedAdvertiser ||
        this.selectedAdvertiser.status === 'ERROR' ||
        this.form.browser.length === 0
      ) {
        return false
      }

      return true
    },
    /**
     * Check if creative step is complete
    */
    isCreativeStepComplete () {
      if (
        !this.form.creative.video_id ||
        (!this.form.creative.headline || this.form.creative.headline > 90) ||
        (!this.form.creative.description || this.form.creative.description > 70) ||
        (!this.form.creative.call_to_action || this.form.creative.call_to_action > 10) ||
        !this.form.creative.url
      ) return false
      return true
    },
    /**
     * Check if is edit page
     */
    isEditPage () {
      return this.$route.name === 'campaigns.edit'
    },
    /**
     * Is form complete
     */
    isFormComplete () {
      return (
        this.isCampaignStepComplete,
        this.isCreativeStepComplete
      )
    },
    /**
     * Get previous step
     */
    previousStep () {
      switch (this.step) {
        case 'campaign':
          return null
        case 'creative':
          return 'campaign'
        case 'tracking':
          return 'creative'
        default:
          return null
      }
    },
    /**
     * Router name
     */
    routeName () {
      return this.$route.name
    },
    /**
     * Selected advertiser
     */
    selectedAdvertiser () {
      if (!this.form.advertiser_id) return null

      return this.advertisers.find(item => {
        this.money = getMoneyFormat(item.currency)

        return item.id === this.form.advertiser_id
      })
    },
    /**
     * show alert on cities and states selection
     */
    showAlertOnCitiesAndStatesSelection () {
      const selectedStates = this.form.states.map((state) => {
        const stateData = this.states.find((s) => s.id === state.code)
        return stateData?.name || ''
      })

      const selectedCities = this.form.cities.map((city) => {
        const cityData = this.cities.find((c) => c.id === city.code)
        return cityData ? { name: cityData.name, state_name: cityData.state_name } : null
      }).filter(city => city !== null)

      if (selectedStates.length && selectedCities.length) {
        // Check if the cities are within the selected state
        const citiesOutOfState = selectedCities.filter((city) =>
          !selectedStates.includes(city.state_name)
        )

        if (citiesOutOfState.length) {
          return this.$t('region_selection_states_and_cities', {
            states: selectedStates.join(', '),
            cities: [...new Set(citiesOutOfState.map((city) => city.state_name))].join(', ')
          })
        } else {
          return this.$t('region_selection_city_inside_state')
        }
      }

      if (selectedStates.length > 0) {
        return this.$t('region_selection_only_states')
      }

      if (selectedCities.length > 0) {
        return this.$t('region_selection_only_cities')
      }
      return false
    },
    /**
     * State options for select 2 field
     */
    stateOptions () {
      return this.states.map(state => {
        return {
          label: `${state.name} (${state.country_short_name})`,
          code: state.id
        }
      })
    },
    /**
     * Step description
     */
    stepDescription () {
      switch (this.step) {
        case 'campaign':
          return this.$t('create_basic_campaign_settings')
        case 'creative':
          return this.$t('configure_campaign_video')
        case 'tracking':
          return this.$t('configure_tracking_pixels')
        default:
          return ''
      }
    },
    /**
     * calculate campaign end date, minimum value 10 reais per day
     */
    suggestedEndDate () {
      if (!this.form.start_date || !this.form.end_date) return null

      const startDate = moment(this.form.start_date)

      const suggestedEndDate = startDate.clone().add(Math.floor(this.form.amount / this.minimumDailyBudget), 'days').subtract(1, 'day')
      return suggestedEndDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    }
  },
  methods: {
    /**
     * Add all selected advertiser available amount to campaign
     */
    addAllAvailableAmountToCampaign () {
      if (!this.selectedAdvertiser) return
      // If is creation, use full amount
      if (!this.isEditPage) {
        this.form.amount = (this.selectedAdvertiser.available_amount / 100)
      } else {
        // If is edit page, add the amount to what is in the form
        this.form.amount = this.form.amount + (this.selectedAdvertiser.available_amount / 100)
      }
    },
    /**
     * change accordion item
     */
    changeAccordionItem (item) {
      this.$root.$emit('bv::toggle::collapse', item)
    },
    /**
     * Change step of form
     */
    changeStep (step) {
      if (step === 'tracking') this.saveDraft()
      this.step = step
    },
    /**
     * Clear auto save interval
     */
    clearAutoSaveInterval () {
      // this.isAutoSaveEnabled = false
      clearInterval(this.autoSaveInterval)
    },
    /**
     * On click submit button
     */
    clickSubmit () {
      this.submit(false)
    },
    /**
     * Destroy audience
     */
    destroyAudience (index, type) {
      if (type === 'PLATFORM_DATA') this.form.platform_audiences.splice(index, 1)
    },
    /**
     * Remove pixel rule from rules array
     */
    destroyPixelRule (rule) {
      const index = this.form.pixels.findIndex(item => {
        return item.id === rule.id
      })
      this.form.pixels.splice(index, 1)
    },
    /**
     * Get advertisers
     */
    async getAdvertisers () {
      this.advertisersLoader = true
      try {
        const advertisers = await AdvertisersService.getAdvertisers({
          companyId: this.user.company.id,
          itemsPerPage: 100,
          status: 'ACTIVE',
          viewType: 'listing_for_campaign_creation'
        })
        this.advertisers = advertisers.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.advertisersLoader = false
    },
    /**
     * Get advertiser
     */
    async getCampaign () {
      if (!this.baseCampaign) return
      this.campaign = this.baseCampaign
      this.setCampaign(this.baseCampaign)
    },
    /**
     * Get cities
     */
    async getCities () {
      if (this.selectedCountries.length === 0) {
        this.cities = []
        return
      }
      this.citiesLoader = true
      try {
        const cities = await HelpersService.getCities({
          countryId: this.selectedCountries.map(item => {
            return item.code
          })
        })
        this.cities = cities
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.citiesLoader = false
    },
    /**
     * Get countries
     */
    async getCountries () {
      this.countriesLoader = true
      try {
        const countries = await HelpersService.getCountries()
        this.countries = countries
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.countriesLoader = false
    },

    /**
     * Get necessary page content
     */
    getPageContent () {
      this.getAdvertisers()
      this.getCountries()
    },
    /**
     * Get states
     */
    async getStates () {
      if (this.selectedCountries.length === 0) {
        this.states = []
        return
      }
      this.statesLoader = true
      try {
        const states = await HelpersService.getStates({
          country_id: this.selectedCountries.map(item => {
            return item.code
          })
        })
        this.states = states
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.statesLoader = false
    },
    /**
     * Handle audience list selected
     */
    handleAudienceListSelected (audiences, type) {
      if (type === 'PLATFORM_DATA') this.form.platform_audiences = [...audiences]
    },
    /**
     * Handle selected pixel rule
     */
    handleSelectedPixelRule (rule) {
      const index = this.form.pixels.findIndex(item => {
        return item.id === rule.id
      })
      if (index >= 0) return
      this.form.pixels.push(rule)
    },
    /**
     * Load video preview
    */
    loadVideoPreview (id) {
      this.showVideoPreview = true
    },
    /**
     * Open modal to destroy pixel rule
     */
    openDestroyPixelRuleModal (rule) {
      this.selectedPixelRule = rule
      this.$bvModal.show('destroy-pixel-rule-modal')
    },
    /**
     * Save draft
     */
    saveDraft () {
      if (!this.selectedAdvertiser) return
      if (this.campaign && this.campaign.status !== 'DRAFT') return
      this.submit(true)
    },
    /**
     * Select all states
     */
    selectAllStates () {
      const all = []
      this.stateOptions.forEach(item => {
        all.push({
          label: item.label,
          code: item.code
        })
      })
      this.form.states = all
    },
    /**
     * Create auto save interval
     */
    setAutoSaveInterval () {
      // this.isAutoSaveEnabled = true
      this.autoSaveInterval = setInterval(() => {
        this.saveDraft()
      }, 30000)
    },
    /**
     * Set campaign
     */
    setCampaign (campaign) {
      this.form.advertiser_id = campaign.advertiser.id
      this.form.name = campaign.name

      this.form.start_date = campaign.start_date
      this.form.end_date = campaign.end_date
      this.form.amount = campaign.amount / 100
      this.form.pixels = campaign.pixels.map(item => {
        return {
          id: item.id,
          name: item.name,
          objective: item.objective,
          event_name: item.event_name,
          pixel: item.pixel
        }
      })
      this.form.cpm_target_amount = campaign.cpm_target_amount / 100
      this.form.cpv_target_amount = campaign.cpv_target_amount / 100
      this.form.browser = campaign.browser
      this.form.platform_audiences = campaign.audiences.filter(item => {
        return item.type === 'PLATFORM_DATA'
      })
      this.form.states = campaign.states.map(s => {
        return {
          label: s.name,
          code: s.id
        }
      })
      this.form.cities = campaign.cities.map(c => {
        return {
          label: `${c.name} (${c.state_short_name})`,
          code: c.id
        }
      })
      const creative = campaign.creatives.length > 0 ? campaign.creatives[0] : null
      if (creative) {
        this.form.creative.video_id = creative.youtube_id
        this.form.creative.headline = creative.headline
        this.form.creative.description = creative.body_text
        this.form.creative.call_to_action = creative.call_to_action
        this.form.creative.url = creative.url.replace('https://', '').replace('http://', '')
      }

      this.setSelectedCountries()
    },
    /**
     * Set selected countries
     */
    setSelectedCountries () {
      if (!this.campaign) return
      const countries = []
      this.campaign.states.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })
      this.campaign.cities.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })
      this.selectedCountries = this.countries.filter(item => {
        return countries.includes(item.short_name)
      }).map(item => {
        return {
          label: `${item.name} (${item.short_name})`,
          code: item.id
        }
      })
    },
    /**
     * Submit
     */
    async submit (isDraft = false) {
      // this.clearAutoSaveInterval()
      if (isDraft) this.isSavingDraft = true
      else this.loader = true
      try {
        // Create copy of form that can be changed (object assign wont work for multi-layered objects)
        const form = JSON.parse(JSON.stringify(this.form))

        // Audiences
        form.audiences = this.form.platform_audiences.map(item => {
          return item.id
        })
        // Parse amounts
        form.amount = parseInt(form.amount * 100)
        form.cpv_target_amount = parseInt(form.cpv_target_amount * 100)
        form.cities = this.form.cities.map(item => {
          return item.code
        })
        form.states = this.form.states.map(state => {
          return state.code
        })
        form.pixels = this.form.pixels.map(item => {
          return item.id
        })
        form.creative.url = `https://${form.creative.url.replace('https://', '').replace('http://', '')}`

        let campaign = null
        form.is_draft = isDraft
        if (!this.campaign) {
          campaign = await CampaignsService.createCampaign(form)
        } else {
          campaign = await CampaignsService.updateCampaign(this.campaign.id, form)
        }
        if (!isDraft) {
          this.$router.push({ name: 'campaigns.show', params: { id: campaign.id } })
          const notification = {
            notification: {
              type: 'success',
              content: this.$t('campaign_in_creation_process')
            }
          }
          NotificationService.createGlobalSuccessNotification(notification)
        } else {
          this.campaign = campaign
        }
      } catch (error) {
        // console.log(error)
        error.handleGlobally && error.handleGlobally()
      }
      if (isDraft) this.isSavingDraft = false
      else this.loader = false
      // Reenable autosave
      // if (isDraft) this.setAutoSaveInterval()
    }
  }
}
</script>

<style lang="css" src="../../../../../node_modules/vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
  ::v-deep {
    .native-campaign-navigation {
      position: absolute;
      top: 48px;
      left: -100px;
      z-index: 2;
      display: none;

      @include desktop {
        display: block;
      }
    }
  }
</style>
